import { useState } from 'react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Label,
  Badge,
} from '@alertwhale/ui'
import { Smartphone, Copy, CheckCircle2, Loader2, QrCode, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'
import { api } from '@/lib/api'

interface SubscriptionLinkGeneratorProps {
  appId: string
  appName: string
}

export function SubscriptionLinkGenerator({ appId, appName }: SubscriptionLinkGeneratorProps) {
  const [userId, setUserId] = useState('')
  const [externalId, setExternalId] = useState('')
  const [platform, setPlatform] = useState<'ios' | 'android'>('ios')
  const [generatedLink, setGeneratedLink] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [expiresAt, setExpiresAt] = useState('')

  const handleGenerate = async () => {
    if (!userId.trim()) {
      toast.error('User ID is required')
      return
    }

    setIsLoading(true)

    try {
      const response = await api.post(`/dashboard/applications/${appId}/generate-subscription-link`, {
        user_id: userId.trim(),
        external_id: externalId.trim() || undefined,
        platform,
      })

      if (response.success && response.data) {
        setGeneratedLink(response.data.link)
        setExpiresAt(response.data.expires_at)
        toast.success('Subscription link generated!')
      } else {
        toast.error(response.error?.message || 'Failed to generate link')
      }
    } catch (error) {
      toast.error('Failed to generate subscription link')
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink)
      toast.success('Link copied to clipboard!')
    } catch {
      toast.error('Failed to copy link')
    }
  }

  const openInSimulator = () => {
    // For testing in iOS simulator
    if (platform === 'ios') {
      window.open(generatedLink, '_blank')
    } else {
      // For Android, show ADB command
      toast.info('Use ADB command shown below to test on Android')
    }
  }

  return (
    <div className="space-y-4">
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            Generate Subscription Link
          </CardTitle>
          <CardDescription>
            Create a deep link to automatically subscribe users to receive notifications in the mobile app
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Platform Selection */}
          <div className="space-y-2">
            <Label>Platform</Label>
            <div className="flex gap-3">
              <Button
                variant={platform === 'ios' ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => setPlatform('ios')}
              >
                <Smartphone className="h-4 w-4 mr-2" />
                iOS
              </Button>
              <Button
                variant={platform === 'android' ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => setPlatform('android')}
              >
                <Smartphone className="h-4 w-4 mr-2" />
                Android
              </Button>
            </div>
          </div>

          {/* User ID Input */}
          <div className="space-y-2">
            <Label htmlFor="userId">
              User ID <span className="text-destructive">*</span>
            </Label>
            <Input
              id="userId"
              placeholder="user_123"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              className="bg-background/50"
            />
            <p className="text-xs text-muted-foreground">
              The internal user ID in your AlertWhale application
            </p>
          </div>

          {/* External ID Input (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="externalId">
              External ID <span className="text-muted-foreground text-xs">(optional)</span>
            </Label>
            <Input
              id="externalId"
              placeholder="customer_456"
              value={externalId}
              onChange={(e) => setExternalId(e.target.value)}
              className="bg-background/50"
            />
            <p className="text-xs text-muted-foreground">
              Your application's user identifier
            </p>
          </div>

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            disabled={isLoading || !userId.trim()}
            className="w-full"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <QrCode className="h-4 w-4 mr-2" />
                Generate Link
              </>
            )}
          </Button>

          {/* Generated Link Display */}
          {generatedLink && (
            <div className="space-y-4 pt-4 border-t border-border/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <span className="font-semibold">Link Generated</span>
                </div>
                <Badge variant="secondary" className="text-xs">
                  Expires in 5 min
                </Badge>
              </div>

              {/* Link Preview */}
              <div className="bg-muted/30 rounded-lg p-4 space-y-3">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Deep Link URL</Label>
                  <div className="flex gap-2">
                    <Input
                      value={generatedLink}
                      readOnly
                      className="bg-background font-mono text-xs"
                    />
                    <Button onClick={copyToClipboard} variant="outline" size="sm">
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button onClick={openInSimulator} variant="outline" size="sm" className="flex-1">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open in {platform === 'ios' ? 'Simulator' : 'Device'}
                  </Button>
                </div>
              </div>

              {/* Testing Instructions */}
              <Card className="bg-muted/20">
                <CardContent className="pt-6 space-y-3">
                  <h4 className="font-semibold text-sm">Testing Instructions</h4>

                  {platform === 'ios' ? (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">
                        <strong>iOS Simulator:</strong>
                      </p>
                      <code className="block bg-background p-2 rounded text-xs font-mono">
                        xcrun simctl openurl booted "{generatedLink}"
                      </code>

                      <p className="text-xs text-muted-foreground mt-3">
                        <strong>Physical Device:</strong>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Open the link in Safari or Notes app on your device
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">
                        <strong>Android Device/Emulator:</strong>
                      </p>
                      <code className="block bg-background p-2 rounded text-xs font-mono">
                        adb shell am start -a android.intent.action.VIEW -d "{generatedLink}"
                      </code>

                      <p className="text-xs text-muted-foreground mt-3">
                        Or create an HTML file with:
                      </p>
                      <code className="block bg-background p-2 rounded text-xs">
                        {'<a href="' + generatedLink + '">Subscribe</a>'}
                      </code>
                    </div>
                  )}

                  <div className="pt-3 border-t border-border/50">
                    <p className="text-xs text-warning flex items-center gap-2">
                      <CheckCircle2 className="h-3 w-3" />
                      Link expires at: {new Date(expiresAt).toLocaleString()}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </CardContent>
      </Card>

      {/* How It Works */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-lg">How It Works</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-bold text-sm flex-shrink-0">
              1
            </div>
            <div>
              <p className="font-medium text-sm">Generate Subscription Link</p>
              <p className="text-xs text-muted-foreground">
                Enter user ID and select platform to create a secure JWT-signed deep link
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-bold text-sm flex-shrink-0">
              2
            </div>
            <div>
              <p className="font-medium text-sm">User Clicks Link</p>
              <p className="text-xs text-muted-foreground">
                Link opens the AlertWhale mobile app automatically (alertwhale://subscribe)
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-bold text-sm flex-shrink-0">
              3
            </div>
            <div>
              <p className="font-medium text-sm">Auto-Registration</p>
              <p className="text-xs text-muted-foreground">
                App validates JWT, registers device token, and subscribes user - no interaction needed
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-bold text-sm flex-shrink-0">
              4
            </div>
            <div>
              <p className="font-medium text-sm">Ready to Receive</p>
              <p className="text-xs text-muted-foreground">
                User can now receive push notifications and live updates for {appName}
              </p>
            </div>
          </div>

          <div className="bg-primary/5 rounded-lg p-3 mt-4">
            <p className="text-xs text-muted-foreground">
              <strong className="text-foreground">Security:</strong> Links are JWT-signed with 5-minute expiry
              and can only be used once. The device token is securely registered with AlertWhale API.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
